import Box from '@mui/material/Box';
import { observer } from 'mobx-react-lite';
import { useCallback, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { useStores } from '~store';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import { EmbeddedReport } from './EmbeddedReport';
import ReportCategoryList, { REPORT_SETTINGS_SIDEBAR_WIDTH } from './ReportCategoryList';

// The URL exposes the Omni dashboard ID (short, stable, matches Omni's own
// dashboard URL and the value admins see in /admin/report-settings). The
// internal Tread `OmniReport.id` is kept in component state so `EmbeddedReport`
// can call the by-id signed-URL endpoint.
const REPORT_QUERY_PARAM = 'report';

export const OmniReports = observer(() => {
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedContentId = searchParams.get(REPORT_QUERY_PARAM) ?? '';
  const [resolvedReportId, setResolvedReportId] = useState<string>('');

  const { userStore } = useStores();
  const companyId = getEffectiveUserCompanyId(userStore);

  const handleReportSelect = useCallback(
    (contentId: string, reportId: string) => {
      setSearchParams((params) => {
        if (contentId) {
          params.set(REPORT_QUERY_PARAM, contentId);
        } else {
          params.delete(REPORT_QUERY_PARAM);
        }
        return params;
      });
      setResolvedReportId(reportId);
    },
    [setSearchParams],
  );

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'row',
        height: '100%',
        mx: -5,
      }}
    >
      <ReportCategoryList
        companyId={companyId}
        selectedContentId={selectedContentId}
        onReportSelect={handleReportSelect}
        data-testid="report-category-list"
      />
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          width: `calc(100% - ${REPORT_SETTINGS_SIDEBAR_WIDTH}px)`,
          height: '100%',
          flex: 1,
        }}
      >
        <EmbeddedReport
          companyId={companyId}
          reportId={resolvedReportId}
          data-testid="embedded-report"
        />
      </Box>
    </Box>
  );
});
